import { useCallback, useEffect, useRef, useState } from 'react';
import { getVapidPublicKey, subscribePush, unsubscribePush } from '../api/push.js';
import { isIOS } from '../lib/native.js';
import { errorMessage } from '../lib/errors.js';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export interface UseNotifications {
  isSupported: boolean;
  isStandalone: boolean;
  needsInstall: boolean;
  permission: NotificationPermission;
  subscription: PushSubscription | null;
  subscribing: boolean;
  error: string | null;
  enabled: boolean;
  requestPermission: () => Promise<void>;
  unsubscribe: () => Promise<void>;
}

export function useNotifications(): UseNotifications {
  const isSupported =
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window &&
    'serviceWorker' in navigator;

  const isStandalone =
    typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as unknown as { standalone?: boolean }).standalone === true);

  const [permission, setPermission] = useState<NotificationPermission>(
    isSupported ? Notification.permission : 'denied'
  );
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);
  const [subscribing, setSubscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initDone = useRef(false);

  const subscribeToPush = useCallback(async (reg: ServiceWorkerRegistration): Promise<void> => {
    const vapidKey = await getVapidPublicKey();
    if (!vapidKey) throw new Error('VAPID key unavailable');
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
    });
    await subscribePush(sub.toJSON());
    setSubscription(sub);
  }, []);

  useEffect(() => {
    if (!isSupported || permission !== 'granted' || initDone.current) return;
    initDone.current = true;

    navigator.serviceWorker.ready
      .then((reg) =>
        reg.pushManager.getSubscription().then((sub) => {
          if (sub) {
            subscribePush(sub.toJSON()).catch(() => {});
            setSubscription(sub);
          } else {
            subscribeToPush(reg).catch(() => {});
          }
        })
      )
      .catch(() => {});
  }, [isSupported, permission, subscribeToPush]);

  const requestPermission = useCallback(async (): Promise<void> => {
    if (!isSupported) return;
    setSubscribing(true);
    setError(null);
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === 'granted') {
        const reg = await navigator.serviceWorker.ready;
        await subscribeToPush(reg);
      }
    } catch (err) {
      console.error('Push subscribe failed:', err);
      setError(errorMessage(err, 'Subscribe failed'));
    } finally {
      setSubscribing(false);
    }
  }, [isSupported, subscribeToPush]);

  const unsubscribe = useCallback(async (): Promise<void> => {
    if (!subscription) return;
    try {
      await subscription.unsubscribe();
      await unsubscribePush(subscription.endpoint);
    } catch {
      // best-effort; the push manager may have removed it already
    }
    setSubscription(null);
  }, [subscription]);

  // Seul Safari sur iOS réserve le push web à l'app installée ; ailleurs un onglet s'abonne.
  const needsInstall = isSupported && !isStandalone && isIOS();
  const enabled = permission === 'granted' && subscription !== null;

  return {
    isSupported,
    isStandalone,
    needsInstall,
    permission,
    subscription,
    subscribing,
    error,
    enabled,
    requestPermission,
    unsubscribe,
  };
}
